import { CartItems } from '@/hooks/CartContext';
import { calcPrice } from '@/shared/calculatePrices';
import { ClientProps, PriceProductProps, ProductProps, TaxSubstitutionProps } from '@/types';
import {
    ComboCartItemAllocation,
    ComboRequirementGroup,
    findComboRequirementGroup,
    getActiveComboPromotionTypeForCartItem,
    getComboDiscountParticipatingQuantityForCartItem,
    getGrupoLabel,
    normalizeComboName,
} from './comboCartUtils';
import {
    ComboCartLinePriceDeps,
    computeCartLineTotalWithComboDiscount,
    computeCheckoutLineStPricing,
    computeStLineTotalLikeStPrice,
    getComboPriceRecordForCartLine,
    getComboUnitPriceForCartLine,
    getNormalPriceRecordForCartLine,
    roundMoney,
} from './comboCartTotalsUtils';

type DiscountProductLookup = (params: {
    package: string;
    productId: number;
    quantity: number;
}) => { percentual?: number; discountPrice?: Record<string, number> } | undefined;

type FindPriceFn = (
    prices: PriceProductProps[],
    option_uid: string,
    paymentCondition: number
) => PriceProductProps;

/** Mesma regra de preço unitário usada no total do carrinho (flex + condição de pagamento). */
export const resolveCartLineUnitPrice = (
    item: Pick<CartItems, 'sku' | 'option_uid'>,
    prices: PriceProductProps[],
    paymentCondition: number,
    findPrice: FindPriceFn,
    getDiscountProduct: DiscountProductLookup,
    selectedClientScope: Parameters<typeof calcPrice>[0],
    listaTributacao: TaxSubstitutionProps[]
): number | null => {
    const [packing, qtd] = item.option_uid?.split('_') || [];

    const flexEntry = getDiscountProduct({
        package: packing,
        productId: item.sku,
        quantity: Number(qtd),
    });
    const percentualFlex = Number(flexEntry?.percentual ?? 0);
    const rawDiscount = flexEntry?.discountPrice?.[`${paymentCondition}`];
    const price = findPrice(prices, item.option_uid, paymentCondition);

    let finalprice: number | undefined;

    if (rawDiscount !== undefined && rawDiscount !== null) {
        finalprice = Number(rawDiscount);
    } else if (
        percentualFlex !== 0 &&
        price &&
        typeof price.VLR_PRECO === 'number' &&
        listaTributacao?.length
    ) {
        const adjustedRow: PriceProductProps = {
            ...price,
            VLR_PRECO: price.VLR_PRECO + (price.VLR_PRECO / 100) * percentualFlex,
        };
        const calculated = calcPrice(selectedClientScope, [adjustedRow], listaTributacao);
        finalprice = calculated[0]?.VLR_FINAL;
    }

    if (finalprice === undefined || finalprice === null || Number.isNaN(finalprice)) {
        finalprice = price?.VLR_FINAL || price?.VLR_PRECO;
    }

    return finalprice ?? null;
};

export interface CheckoutLineComboPricing {
    lineTotal: number;
    unitPrice: number;
    unitPricePerItem: number;
}

export const buildCheckoutLinePriceWithCombo = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid' | 'quantity'>,
    normalUnitPrice: number,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    packQty: number,
    deps: ComboCartLinePriceDeps
): CheckoutLineComboPricing => {
    const quantity = item.quantity || 0;
    const safePackQty = packQty > 0 ? packQty : 1;

    if (!quantity || !normalUnitPrice) {
        return { lineTotal: 0, unitPrice: 0, unitPricePerItem: 0 };
    }

    const lineTotal = roundMoney(
        allocations?.length
            ? computeCartLineTotalWithComboDiscount(
                  item as CartItems,
                  normalUnitPrice,
                  allocations,
                  baseProduct,
                  paymentCondition,
                  deps
              )
            : normalUnitPrice * quantity
    );

    const unitPrice = lineTotal / quantity;
    const unitPricePerItem = unitPrice / safePackQty;

    return { lineTotal, unitPrice, unitPricePerItem };
};

export type CheckoutPayloadPortionKind = 'combo' | 'regular';

export interface CheckoutPayloadPortion {
    kind: CheckoutPayloadPortionKind;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
    unitPricePerItem: number;
    /** Preço base de integração por unidade (já dividido pela embalagem). */
    integrationPrice: number;
    stLineTotal: number;
    stPerPackaging: number;
    promotionType: string | null;
    reason: string;
    priceRecord: PriceProductProps | null;
}

const resolveIntegrationPriceFromRecord = (
    record: PriceProductProps | null | undefined,
    packQty: number,
    fallback: number
): number => {
    const basePrice = Number(record?.VLR_PRECO_CALCULADO);
    const safePackQty = packQty > 0 ? packQty : 1;
    if (Number.isFinite(basePrice) && basePrice > 0) {
        return basePrice / safePackQty;
    }
    return fallback;
};

/**
 * Separa uma linha do carrinho em parcelas de payload (combo com desconto vs restante
 * sem desconto). Evita enviar preço médio e promotion_type no produto extra.
 */
export const buildCheckoutPayloadPortionsForCartItem = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid' | 'quantity'>,
    normalUnitPrice: number,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    packQty: number,
    deps: ComboCartLinePriceDeps,
    clientForTax: ClientProps,
    listaTributacao: TaxSubstitutionProps[],
    percentualFlex: number,
    fallbackIntegrationPrice: number,
    combos: Array<{ id_campanha?: string; grupos_exigencia?: ComboRequirementGroup[] }>,
    isComboCompleteById: (idCampanha: string) => boolean
): CheckoutPayloadPortion[] => {
    const quantity = item.quantity || 0;
    const safePackQty = packQty > 0 ? packQty : 1;

    if (!quantity) return [];

    const discountQty = getComboDiscountParticipatingQuantityForCartItem(item, allocations);
    const regularQty = Math.max(quantity - discountQty, 0);
    const comboUnitPrice =
        discountQty > 0
            ? getComboUnitPriceForCartLine(item, allocations, baseProduct, paymentCondition, deps)
            : null;
    const hasComboPortion = discountQty > 0 && comboUnitPrice != null;
    const portions: CheckoutPayloadPortion[] = [];

    const normalRecord = getNormalPriceRecordForCartLine(item, baseProduct, paymentCondition, deps);
    const comboRecord = hasComboPortion
        ? getComboPriceRecordForCartLine(item, allocations, baseProduct, paymentCondition, deps)
        : null;

    if (hasComboPortion) {
        const unitPrice = Number(comboUnitPrice);
        const lineTotal = roundMoney(unitPrice * discountQty);
        const integrationPrice = resolveIntegrationPriceFromRecord(
            comboRecord,
            safePackQty,
            fallbackIntegrationPrice
        );
        const stLineTotal = computeStLineTotalLikeStPrice(
            comboRecord,
            discountQty,
            clientForTax,
            listaTributacao,
            percentualFlex
        );
        const stPerPackaging = computeStLineTotalLikeStPrice(
            comboRecord,
            1,
            clientForTax,
            listaTributacao,
            percentualFlex
        );

        portions.push({
            kind: 'combo',
            quantity: discountQty,
            unitPrice,
            lineTotal,
            unitPricePerItem: unitPrice / safePackQty,
            integrationPrice,
            stLineTotal: Number(stLineTotal.toFixed(2)),
            stPerPackaging: Number(stPerPackaging.toFixed(2)),
            promotionType: getActiveComboPromotionTypeForCartItem(
                item,
                allocations,
                isComboCompleteById
            ),
            reason: buildComboReasonForCartItem(allocations, combos),
            priceRecord: comboRecord,
        });
    }

    const remainingQty = hasComboPortion ? regularQty : quantity;
    if (remainingQty > 0 && normalUnitPrice > 0) {
        const unitPrice = normalUnitPrice;
        const lineTotal = roundMoney(unitPrice * remainingQty);
        const integrationPrice = resolveIntegrationPriceFromRecord(
            normalRecord,
            safePackQty,
            fallbackIntegrationPrice
        );
        const stLineTotal = computeStLineTotalLikeStPrice(
            normalRecord,
            remainingQty,
            clientForTax,
            listaTributacao,
            percentualFlex
        );
        const stPerPackaging = computeStLineTotalLikeStPrice(
            normalRecord,
            1,
            clientForTax,
            listaTributacao,
            percentualFlex
        );

        portions.push({
            kind: 'regular',
            quantity: remainingQty,
            unitPrice,
            lineTotal,
            unitPricePerItem: unitPrice / safePackQty,
            integrationPrice,
            stLineTotal: Number(stLineTotal.toFixed(2)),
            stPerPackaging: Number(stPerPackaging.toFixed(2)),
            promotionType: null,
            reason: '',
            priceRecord: normalRecord,
        });
    }

    // Fallback: se não gerou parcela (ex.: preço combo nulo), mantém linha única mesclada.
    if (!portions.length) {
        const blended = buildCheckoutLinePriceWithCombo(
            item,
            normalUnitPrice,
            allocations,
            baseProduct,
            paymentCondition,
            safePackQty,
            deps
        );
        const stPricing = computeCheckoutLineStPricing(
            item as CartItems,
            allocations,
            baseProduct,
            paymentCondition,
            deps,
            clientForTax,
            listaTributacao,
            percentualFlex
        );
        const integrationPrice = buildCheckoutIntegrationPriceWithCombo(
            item,
            allocations,
            baseProduct,
            paymentCondition,
            deps,
            fallbackIntegrationPrice
        );

        portions.push({
            kind: discountQty > 0 ? 'combo' : 'regular',
            quantity,
            unitPrice: blended.unitPrice,
            lineTotal: blended.lineTotal,
            unitPricePerItem: blended.unitPricePerItem,
            integrationPrice,
            stLineTotal: stPricing.stLineTotal,
            stPerPackaging: stPricing.stPerPackaging,
            promotionType:
                discountQty > 0
                    ? getActiveComboPromotionTypeForCartItem(item, allocations, isComboCompleteById)
                    : null,
            reason: discountQty > 0 ? buildComboReasonForCartItem(allocations, combos) : '',
            priceRecord: comboRecord || normalRecord,
        });
    }

    return portions;
};

/**
 * Retorna o preço base por item que deve ser integrado no pedido.
 * Para linhas parcialmente atendidas por combo, combina os preços base de
 * cada parcela proporcionalmente à quantidade de embalagens.
 */
export const buildCheckoutIntegrationPriceWithCombo = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid' | 'quantity'>,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps,
    fallbackIntegrationPrice: number
): number => {
    const quantity = item.quantity || 0;
    const discountQty = getComboDiscountParticipatingQuantityForCartItem(item, allocations);

    if (!quantity || discountQty <= 0) return fallbackIntegrationPrice;

    const comboRecord = getComboPriceRecordForCartLine(
        item,
        allocations,
        baseProduct,
        paymentCondition,
        deps
    );
    const comboBasePrice = Number(comboRecord?.VLR_PRECO_CALCULADO);

    if (!Number.isFinite(comboBasePrice) || comboBasePrice <= 0) {
        return fallbackIntegrationPrice;
    }

    const packagingQty = Number(
        comboRecord?.QTD_MULTIPLO_VENDA || baseProduct?.EMBALAGEM?.find((packaging) => {
            const [pack, qty] = item.option_uid?.split('_') || [];
            return (
                packaging.SG_EMBALAGEM === pack &&
                Number(packaging.QTD_EMBALAGEM || packaging.QTD_MULTIPLO_VENDA) === Number(qty)
            );
        })?.QTD_MULTIPLO_VENDA || item.option_uid?.split('_')[1] || 1
    );
    const safePackagingQty = packagingQty > 0 ? packagingQty : 1;
    const comboIntegrationPrice = comboBasePrice / safePackagingQty;

    if (discountQty >= quantity) return comboIntegrationPrice;

    const normalRecord = getNormalPriceRecordForCartLine(item, baseProduct, paymentCondition, deps);
    const normalBasePrice = Number(normalRecord?.VLR_PRECO_CALCULADO);
    const normalIntegrationPrice =
        Number.isFinite(normalBasePrice) && normalBasePrice > 0
            ? normalBasePrice / safePackagingQty
            : fallbackIntegrationPrice;
    const regularQty = quantity - discountQty;

    return (comboIntegrationPrice * discountQty + normalIntegrationPrice * regularQty) / quantity;
};

/** Texto de reason para itens em combo ativo: "COMBO 2 — Grupo 2.0". */
export const buildComboReasonForCartItem = (
    allocations: ComboCartItemAllocation[] | undefined,
    combos: Array<{ id_campanha?: string; grupos_exigencia?: ComboRequirementGroup[] }>
): string => {
    if (!allocations?.length) return '';

    const activeAllocations = allocations.filter((entry) => (entry.allocatedPackaging || 0) > 0);
    if (!activeAllocations.length) return '';

    const labels = new Set<string>();

    activeAllocations.forEach((allocation) => {
        const combo = combos.find((entry) => entry.id_campanha === allocation.id_campanha);
        const comboName =
            allocation.nome_campanha ||
            normalizeComboName(combo || { id_campanha: allocation.id_campanha });
        const grupo = findComboRequirementGroup(combo?.grupos_exigencia, allocation.grupoId);
        const grupoLabel = getGrupoLabel(grupo, allocation.grupoId || 'Grupo');
        labels.add(`${comboName} — ${grupoLabel}`);
    });

    return Array.from(labels).join(' | ');
};

export interface CheckoutDisplayPricing extends CheckoutLineComboPricing {
    originalLineTotal: number;
    hasComboDiscount: boolean;
    stLineTotal: number;
    stPerPackaging: number;
    priceRecordForSt: PriceProductProps | null;
    hasComboStSplit: boolean;
}

export interface CheckoutDisplayPricingContext {
    paymentCode: number;
    findPrice: FindPriceFn;
    getDiscountProduct: DiscountProductLookup;
    selectedClientScope: Parameters<typeof calcPrice>[0];
    selectedClient: Parameters<typeof calcPrice>[0];
    listaTributacao: TaxSubstitutionProps[];
    checkoutComboPriceDeps: ComboCartLinePriceDeps;
    getAllocations?: (uid: string) => ComboCartItemAllocation[] | undefined;
}

const toPriceList = (rawPrecos: ProductProps['PRECO']): PriceProductProps[] => {
    if (!rawPrecos) return [];
    return Array.isArray(rawPrecos) ? rawPrecos : Object.values(rawPrecos);
};

export const buildCheckoutDisplayPricingForEntry = (
    entry: { item: CartItems; product: ProductProps },
    context: CheckoutDisplayPricingContext
): CheckoutDisplayPricing => {
    const { item, product } = entry;
    const [, qtdEmb] = item.option_uid?.split('_') || [];
    const packQty = Number(qtdEmb) || 1;
    const quantity = item.quantity || 0;
    const prices = toPriceList(product.PRECO);

    const normalUnitPrice =
        resolveCartLineUnitPrice(
            item,
            prices,
            context.paymentCode,
            context.findPrice,
            context.getDiscountProduct,
            context.selectedClientScope,
            context.listaTributacao
        ) ?? 0;

    const allocations = context.getAllocations?.(item.uid);
    const comboLinePricing = buildCheckoutLinePriceWithCombo(
        item,
        normalUnitPrice,
        allocations,
        product,
        context.paymentCode,
        packQty,
        context.checkoutComboPriceDeps
    );

    const originalLineTotal = normalUnitPrice * quantity;

    const percentualFlex = Number(
        context.getDiscountProduct({
            package: item.option_uid?.split('_')[0] || '',
            productId: item.sku,
            quantity: packQty,
        })?.percentual ?? 0
    );

    const stPricing = computeCheckoutLineStPricing(
        item,
        allocations,
        product,
        context.paymentCode,
        context.checkoutComboPriceDeps,
        context.selectedClient,
        context.listaTributacao,
        percentualFlex
    );

    const discountQty = getComboDiscountParticipatingQuantityForCartItem(item, allocations);
    const comboRecord =
        discountQty > 0
            ? getComboPriceRecordForCartLine(
                  item,
                  allocations,
                  product,
                  context.paymentCode,
                  context.checkoutComboPriceDeps
              )
            : null;
    const normalRecord = getNormalPriceRecordForCartLine(
        item,
        product,
        context.paymentCode,
        context.checkoutComboPriceDeps
    );
    const hasComboDiscount = comboLinePricing.lineTotal < originalLineTotal;
    const regularQty = Math.max((item.quantity || 0) - discountQty, 0);
    const hasComboStSplit = discountQty > 0 && regularQty > 0;
    const priceRecordForSt =
        hasComboDiscount && comboRecord ? comboRecord : normalRecord;

    return {
        ...comboLinePricing,
        originalLineTotal,
        hasComboDiscount,
        stLineTotal: stPricing.stLineTotal,
        stPerPackaging: stPricing.stPerPackaging,
        priceRecordForSt,
        hasComboStSplit,
    };
};

export const enrichCheckoutProductListWithPricing = <T extends { item: CartItems; product: ProductProps }>(
    productList: T[],
    context: CheckoutDisplayPricingContext
): Array<T & { checkoutPricing: CheckoutDisplayPricing }> =>
    productList.map((entry) => ({
        ...entry,
        checkoutPricing: buildCheckoutDisplayPricingForEntry(entry, context),
    }));

export const sumCheckoutItemLineTotals = (items: Array<{ price?: string | number }>): number =>
    roundMoney(items.reduce((sum, item) => sum + Number(item.price || 0), 0));

export const checkoutTotalMatchesCart = (
    itemsTotal: number,
    cartTotal: number,
    tolerance = 0.02
): boolean => Math.abs(itemsTotal - cartTotal) <= tolerance;
